import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import { useInlineRowActions, ROW_ACTIONS_MENU_HEIGHT } from '../../../Hooks/useInlineRowActions';
import { useConfigurationListPagination } from '../../../Hooks/useConfigurationListPagination';
import { urlNameSegment } from '../../../utils/slugify';
import {
  AdminPageTitle,
  AdminPanelTitle,
  AdminTableHeader,
  AdminTableBody,
  AdminTableMeta,
  AdminStatusBadge,
  AdminRowMenuItem,
  AdminRowActionsButton,
  AdminFixedRowActionsMenu,
  AdminRowMenuIcon,
  AdminSupportingText,
  AdminHeaderAction,
  ADMIN_PAGE_TITLE_CLASS,
  ADMIN_TABLE_CLASS,
} from '../shared/adminUx';
import {
  QUOTE_MATCHING_DISTRIBUTION_ROUTE,
  buildSampleQuoteMatchingRules,
  formatConditionsDisplay,
  formatQuoteHistoryDisplay,
  formatQuoteMatchingDate,
  getQuoteMatchingStatusLabel,
} from '../../../data/quoteMatchingDistributionConfig';

const tableCols =
  'minmax(72px, 0.55fr) minmax(140px, 1.2fr) minmax(120px, 1.1fr) minmax(88px, 0.65fr) minmax(100px, 0.7fr) minmax(84px, 0.55fr) minmax(100px, 0.75fr) minmax(84px, 0.55fr) minmax(56px, 0.22fr)';

const TABLE_MIN_WIDTH = '1180px';

const QuoteMatchingDistribution = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [rules, setRules] = useState(() => buildSampleQuoteMatchingRules());
  const [feedback, setFeedback] = useState('');

  const {
    rowActionsOpen,
    rowActionsRefs,
    tableScrollRef,
    rowActionsMenuLayout,
    rowActionsMenuRef,
    rowActionsMenuPlacementReady,
    toggleRowActions,
    closeRowActions,
  } = useInlineRowActions(ROW_ACTIONS_MENU_HEIGHT.DOUBLE);

  const {
    currentPage,
    setCurrentPage,
    totalPages,
    paginatedItems: paginatedRules,
    rangeStart,
    rangeEnd,
    totalCount,
    showPagination,
  } = useConfigurationListPagination(rules);

  useEffect(() => {
    const incoming = location.state?.rules;
    const message = location.state?.feedback;
    if (Array.isArray(incoming) || message) {
      if (Array.isArray(incoming)) setRules(incoming);
      if (message) setFeedback(message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const openAdd = () => {
    navigate(`${QUOTE_MATCHING_DISTRIBUTION_ROUTE}/add`, { state: { existingRules: rules } });
  };

  const openRuleView = (rule) => {
    navigate(`${QUOTE_MATCHING_DISTRIBUTION_ROUTE}/${urlNameSegment(rule.ruleName)}`, {
      state: { rule, viewMode: true, existingRules: rules },
    });
  };

  const openRuleEdit = (rule) => {
    navigate(`${QUOTE_MATCHING_DISTRIBUTION_ROUTE}/${urlNameSegment(rule.ruleName)}/edit`, {
      state: { rule, existingRules: rules },
    });
  };

  return (
    <AdminLayout>
      <div className="min-w-0">
        <div
          className={`flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 ${ADMIN_PAGE_TITLE_CLASS}`}
        >
          <AdminPageTitle className="!mb-0">Quote Matching & Distribution</AdminPageTitle>
          <AdminHeaderAction onClick={openAdd}>Add Rule</AdminHeaderAction>
        </div>

        {feedback ? (
          <AdminSupportingText className="text-freshgreen mb-4">{feedback}</AdminSupportingText>
        ) : null}

        <DashboardMainContainer className={`${ADMIN_TABLE_CLASS} max-w-full min-w-0`}>
          <div ref={tableScrollRef} className="overflow-x-auto">
            {rules.length === 0 ? (
              <div className="py-10 px-4 text-center space-y-4">
                <AdminPanelTitle className="text-offblack">No Quote Matching rules found</AdminPanelTitle>
                <div className="flex justify-center pt-2">
                  <AdminHeaderAction onClick={openAdd}>Add Rule</AdminHeaderAction>
                </div>
              </div>
            ) : (
              <>
                <div
                  className="grid w-full py-3 text-offblack border-b border-lightgray px-4 gap-x-3 gap-y-2"
                  style={{ gridTemplateColumns: tableCols, minWidth: TABLE_MIN_WIDTH }}
                >
                  {[
                    'Rule ID',
                    'Rule Name',
                    'Conditions',
                    'Quote History',
                    'Effective Status',
                    'Date Added',
                    'Last Updated By',
                    'Last Updated',
                    'Actions',
                  ].map((header) => (
                    <div
                      key={header}
                      className={`flex items-center min-w-0 ${
                        header === 'Actions' ? 'justify-center shrink-0' : ''
                      }`}
                    >
                      <AdminTableHeader className="truncate">{header}</AdminTableHeader>
                    </div>
                  ))}
                </div>

                {paginatedRules.map((rule) => {
                  const statusLabel = getQuoteMatchingStatusLabel(rule.status);
                  const conditionsLabel = formatConditionsDisplay(rule.conditions);

                  return (
                    <div
                      key={rule.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => openRuleView(rule)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openRuleView(rule);
                        }
                      }}
                      className="grid w-full py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-x-3 gap-y-2 cursor-pointer rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-primaryblue focus-visible:ring-offset-1"
                      style={{ gridTemplateColumns: tableCols, minWidth: TABLE_MIN_WIDTH }}
                    >
                      <div className="flex items-center min-w-0 overflow-hidden">
                        <AdminTableMeta className="truncate block" title={rule.id}>
                          {rule.id}
                        </AdminTableMeta>
                      </div>
                      <div className="flex items-center min-w-0 overflow-hidden">
                        <AdminTableBody className="truncate block" title={rule.ruleName}>
                          {rule.ruleName}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0 overflow-hidden">
                        <AdminTableBody className="truncate block" title={conditionsLabel}>
                          {conditionsLabel}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0 overflow-hidden">
                        <AdminTableBody className="truncate block">
                          {formatQuoteHistoryDisplay(rule.quoteHistoryDays)}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0 shrink-0">
                        <AdminStatusBadge status={statusLabel} label={statusLabel} />
                      </div>
                      <div className="flex items-center min-w-0 overflow-hidden">
                        <AdminTableBody className="truncate block">
                          {formatQuoteMatchingDate(rule.createdOn)}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0 overflow-hidden">
                        <AdminTableBody className="truncate block">
                          {rule.updatedBy || '—'}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0 overflow-hidden">
                        <AdminTableBody className="truncate block">
                          {formatQuoteMatchingDate(rule.updatedOn)}
                        </AdminTableBody>
                      </div>
                      <div
                        className="flex items-center justify-center shrink-0 overflow-visible"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div
                          className="relative inline-flex"
                          ref={(el) => {
                            rowActionsRefs.current[rule.id] = el;
                          }}
                        >
                          <AdminRowActionsButton
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleRowActions(rule.id);
                            }}
                            aria-expanded={!!rowActionsOpen[rule.id]}
                            title="Actions"
                            aria-label="Row actions"
                          >
                            <AdminRowMenuIcon />
                          </AdminRowActionsButton>
                          {rowActionsOpen[rule.id] && rowActionsMenuLayout && (
                            <AdminFixedRowActionsMenu
                              layout={rowActionsMenuLayout}
                              menuRef={rowActionsMenuRef}
                              placementReady={rowActionsMenuPlacementReady}
                            >
                              <AdminRowMenuItem
                                onClick={() => {
                                  closeRowActions();
                                  openRuleView(rule);
                                }}
                              >
                                View
                              </AdminRowMenuItem>
                              <AdminRowMenuItem
                                onClick={() => {
                                  closeRowActions();
                                  openRuleEdit(rule);
                                }}
                              >
                                Edit
                              </AdminRowMenuItem>
                            </AdminFixedRowActionsMenu>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>

        </DashboardMainContainer>

        <ConfigurationListPaginationFooter
          show={showPagination}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          totalCount={totalCount}
          currentPage={currentPage}
          totalPages={totalPages}
          onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          goToPage={setCurrentPage}
        />
      </div>
    </AdminLayout>
  );
};

export default QuoteMatchingDistribution;
